/**
 * @module app.um
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { AuthService } from 'app/um/services/auth-service';
import { apiCall } from 'app/shared/lib/api/api';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import * as UM from 'app/um/models/essette-api';


@autoinject
export class AuthQueueHistoryGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.oneWay, changeHandler: 'reload' })
    public authId: string | null = null;

    protected queueHistory: ReadonlyArray<UM.AuthQueueHistory> = [];

    private sorting: Sorting = new Sorting();
    protected resultCount: number | null = 0;
    protected pageSize: number = 10;


    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(private readonly service: AuthService, ...rest: any[]) {
        super(...rest);
    }



    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }


    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        if(this.authId) {
            return await this.loadAuthQueueHistory(this.authId);
        }
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadAuthQueueHistory(authId: string): Promise<void> {

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting
        };

        const includes: string[] = ['queue', 'metadata.started.byUser', 'metadata.ended.byUser'];

        try {
            this.state.beginLoad();
            const response = await this.service.loadQueueHistoryByAuth(authId, options, includes);
            this.queueHistory = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

}
